import React, { useState, useEffect, useRef } from 'react'
import styles from './MusicPage.module.scss'
import { getArtists, getArtist, getAlbums, getAlbum, getTracks, listenTrack } from '../../../service/MusicService'
import { addItemToPanel, getMyPanel } from '../../../service/PersonalPanelService'
import Loader from '../../../components/Loader/Loader'
import { WarningIcon } from '../../../components/CustomIcons'

// Icons for player
const PlayIcon = ({size=24}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
const PauseIcon = ({size=24}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
const SkipBackIcon = ({size=24}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
const SkipForwardIcon = ({size=24}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
const VolumeIcon = ({size=24}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
const SearchIcon = ({size=20}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>

const MusicPage = ({ embedded = false, goBack }) => {
  const [view, setView] = useState('home') // home, artist, album
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Data State
  const [artists, setArtists] = useState([])
  const [albums, setAlbums] = useState([])
  const [tracks, setTracks] = useState([])
  const [panelMusicIds, setPanelMusicIds] = useState(new Set())
  
  // Pagination State for Tracks
  const [trackPage, setTrackPage] = useState(1)
  const [hasMoreTracks, setHasMoreTracks] = useState(true)
  const [loadingMoreTracks, setLoadingMoreTracks] = useState(false)

  // Search State
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState({ albums: [], tracks: [] }) 
  
  // Selection State
  const [selectedArtist, setSelectedArtist] = useState(null)
  const [selectedAlbum, setSelectedAlbum] = useState(null)
  
  // Player State
  const [currentTrack, setCurrentTrack] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.5)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playlist, setPlaylist] = useState([]) // Current queue
  
  const audioRef = useRef(new Audio())

  useEffect(() => {
    loadHomeData()
    loadPanelMusicIds()
  }, [])

  const loadPanelMusicIds = async () => {
    try {
      const panel = await getMyPanel()
      const musicIds = panel.data
        .filter((item) => item.item_type === 'music')
        .map((item) => String(item.item_id))
      setPanelMusicIds(new Set(musicIds))
    } catch (error) {
      console.error('Error loading panel music ids:', error)
    }
  }

  // Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length > 0) {
        setIsSearching(true)
        try {
            const [albumsRes, tracksRes] = await Promise.all([
                getAlbums({ search: searchQuery }),
                getTracks({ search: searchQuery })
            ])
            setSearchResults({
                albums: albumsRes.data.results || albumsRes.data,
                tracks: tracksRes.data.results || tracksRes.data
            })
        } catch (err) {
            console.error(err)
        }
      } else {
        setIsSearching(false)
        setSearchResults({ albums: [], tracks: [] })
      }
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery])

  useEffect(() => {
    // Audio Player setup
    const audio = audioRef.current;
    
    // Set initial volume
    audio.volume = volume;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => playNext();

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, []);

  const loadHomeData = async () => {
    setLoading(true)
    try {
      // Load initial data including first page of tracks
      const [artistsRes, albumsRes, tracksRes] = await Promise.all([
        getArtists(),
        getAlbums(),
        getTracks({ page: 1 })
      ])
      
      setArtists(artistsRes.data.results || artistsRes.data)
      setAlbums(albumsRes.data.results || albumsRes.data)
      
      const tracksData = tracksRes.data.results || tracksRes.data
      setTracks(tracksData)
      setHasMoreTracks(!!tracksRes.data.next)
      
      setError(null)
    } catch (err) {
      setError('Не удалось загрузить музыкальную библиотеку')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadMoreTracks = async () => {
      if (loadingMoreTracks || !hasMoreTracks) return;
      setLoadingMoreTracks(true);
      try {
          const nextPage = trackPage + 1;
          const res = await getTracks({ page: nextPage });
          const newTracks = res.data.results || res.data;
          
          setTracks(prev => [...prev, ...newTracks]);
          setTrackPage(nextPage);
          setHasMoreTracks(!!res.data.next);
      } catch (err) {
          console.error(err);
      } finally {
          setLoadingMoreTracks(false);
      }
  }

  const handleAddMusicToPanel = async (track, e) => {
    e.stopPropagation()
    if (!localStorage.getItem('token')) {
      window.dispatchEvent(new CustomEvent('openAuth', { detail: true }))
      return
    }

    try {
      const panelId = String(track.id)
      await addItemToPanel('music', panelId, {
        title: track.title,
        artist: track.artist_name || 'Unknown',
        duration: track.duration,
        audio_file: track.audio_file
      })
      setPanelMusicIds((prev) => new Set(prev).add(panelId))
    } catch (error) {
      if (error?.response?.status === 400) {
        setPanelMusicIds((prev) => new Set(prev).add(String(track.id)))
      } else {
        console.error('Error adding music to panel:', error)
      }
    }
  }

  const handleArtistClick = async (artistId) => {
    setLoading(true)
    try {
      const res = await getArtist(artistId)
      setSelectedArtist(res.data)
      // Artist serializer includes albums
      setView('artist')
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAlbumClick = async (albumId) => {
    setLoading(true)
    try {
      const res = await getAlbum(albumId)
      setSelectedAlbum(res.data)
      // Album serializer includes tracks
      setView('album')
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const playTrack = (track, newPlaylist = null) => {
    if (newPlaylist) {
      setPlaylist(newPlaylist);
    }
    
    // If playing same track, just toggle
    if (currentTrack && currentTrack.id === track.id) {
        togglePlay();
        return;
    }

    const audio = audioRef.current;
    setCurrentTrack(track);
    audio.src = track.audio_file;
    audio.play()
      .then(() => {
        setIsPlaying(true);
        listenTrack(track.id); // Register listen count
      })
      .catch(e => console.error("Playback failed", e));
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (currentTrack) {
        audio.play().catch(e => console.error("Playback failed", e));
        setIsPlaying(true);
      }
    }
  };

  const playNext = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    if (currentIndex < playlist.length - 1) {
      playTrack(playlist[currentIndex + 1]);
    } else {
        // Optionally loop or stop
        setIsPlaying(false);
    }
  };

  const playPrev = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    if (currentIndex > 0) {
      playTrack(playlist[currentIndex - 1]);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    audioRef.current.volume = val;
  };

  const handleSeek = (e) => {
    const val = parseFloat(e.target.value);
    audioRef.current.currentTime = val;
    setCurrentTime(val);
  };
  
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min}:${sec < 10 ? '0' + sec : sec}`;
  }

  const handleBack = () => {
    if (view === 'album') {
       if (selectedArtist && selectedAlbum && selectedAlbum.artist === selectedArtist.id) {
         setView('artist');
       } else {
         setView('home');
       }
       setSelectedAlbum(null);
    } else if (view === 'artist') {
      setView('home');
      setSelectedArtist(null);
    }
  };

  // ----- Render Components -----

  const renderHome = () => {
    if (isSearching) {
        return (
            <div className={styles.searchResults}>
                <h2 className={styles.sectionTitle} style={{marginTop: '20px'}}>Результаты поиска: "{searchQuery}"</h2>
                
                {searchResults.albums.length === 0 && searchResults.tracks.length === 0 && (
                     <div style={{color: '#b3b3b3', marginTop: '20px'}}>Ничего не найдено</div>
                )}

                {searchResults.albums.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Альбомы</h3>
                        <div className={styles.grid}>
                             {searchResults.albums.map(album => (
                                <div key={album.id} className={styles.albumCard} onClick={() => handleAlbumClick(album.id)}>
                                    <div className={styles.albumImageWrapper}>
                                        <img src={album.cover || '/placeholder-album.png'} alt={album.title} className={styles.albumImage} />
                                    </div>
                                    <div className={styles.albumTitle}>{album.title}</div>
                                    <div className={styles.albumArtist}>{album.artist_name}</div>
                                </div>
                             ))}
                        </div>
                    </div>
                )}

                {searchResults.tracks.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Треки</h3>
                        <div className={styles.trackList}>
                             {searchResults.tracks.map((track, index) => (
                                 <div 
                                    key={track.id} 
                                    className={`${styles.trackRow} ${currentTrack?.id === track.id ? styles.active : ''}`}
                                    onClick={() => playTrack(track, searchResults.tracks)}
                                >
                                    <div className={styles.trackNumberContainer}>
                                        <span className={styles.trackNumber}>{index + 1}</span>
                                        <span className={currentTrack?.id === track.id && isPlaying ? styles.playingIcon : styles.playIcon}>
                                             {currentTrack?.id === track.id && isPlaying ? <PauseIcon size={16}/> : <PlayIcon size={16}/>}
                                        </span>
                                    </div>
                                    <div className={styles.trackInfo}>
                                        <div className={styles.trackTitle}>{track.title}</div>
                                        <div className={styles.trackArtist}>{track.artist_name || 'Unknown'}</div>
                                    </div>
                                    <div className={styles.trackDuration}>
                                        {formatTime(track.duration)}
                                    </div>
                                    <button
                                        className={`${styles.addToPanelButton} ${panelMusicIds.has(String(track.id)) ? styles.addToPanelButtonAdded : ''}`}
                                        onClick={(e) => handleAddMusicToPanel(track, e)}
                                    >
                                        {panelMusicIds.has(String(track.id)) ? 'Уже в панели' : 'В панель'}
                                    </button>
                                </div>
                             ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Популярные Исполнители</h2>
        </div>
        <div className={styles.grid}>
          {artists.map(artist => (
            <div key={artist.id} className={styles.artistCard} onClick={() => handleArtistClick(artist.id)}>
              <img src={artist.image || '/placeholder-avatar.png'} alt={artist.name} className={styles.artistImage} />
              <div className={styles.artistName}>{artist.name}</div>
              <div className={styles.artistLabel}>Исполнитель</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Новые Альбомы</h2>
        </div>
        <div className={styles.grid}>
          {albums.map(album => (
            <div key={album.id} className={styles.albumCard} onClick={() => handleAlbumClick(album.id)}>
              <div className={styles.albumImageWrapper}>
                  <img src={album.cover || '/placeholder-album.png'} alt={album.title} className={styles.albumImage} />
              </div>
              <div className={styles.albumTitle}>{album.title}</div>
              <div className={styles.albumArtist}>{album.artist_name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
          <div className={styles.sectionHeader}>
             <h2 className={styles.sectionTitle}>Все Треки</h2>
          </div>
           <div className={styles.trackList}>
            <div className={styles.trackRow} style={{cursor: 'default', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '10px'}}>
                <div className={styles.trackNumberContainer} style={{color: '#b3b3b3', fontSize: '14px'}}>#</div>
                <div className={styles.trackInfo} style={{color: '#b3b3b3', fontSize: '14px', textTransform: 'uppercase'}}>Название</div>
                <div style={{color: '#b3b3b3', fontSize: '14px'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg></div>
            </div>
            {tracks.map((track, index) => (
                <div 
                    key={track.id} 
                    className={`${styles.trackRow} ${currentTrack?.id === track.id ? styles.active : ''}`}
                    onClick={() => playTrack(track, tracks)}
                >
                    <div className={styles.trackNumberContainer}>
                        <span className={styles.trackNumber}>{index + 1}</span>
                        <span className={currentTrack?.id === track.id && isPlaying ? styles.playingIcon : styles.playIcon}>
                             {currentTrack?.id === track.id && isPlaying ? <PauseIcon size={16}/> : <PlayIcon size={16}/>}
                        </span>
                    </div>
                    <div className={styles.trackInfo}>
                        <div className={styles.trackTitle}>{track.title}</div>
                        <div className={styles.trackArtist}>{track.artist_name || 'Unknown'}</div>
                    </div>
                    <div className={styles.trackDuration}>
                        {formatTime(track.duration)}
                    </div>
                    <button
                        className={`${styles.addToPanelButton} ${panelMusicIds.has(String(track.id)) ? styles.addToPanelButtonAdded : ''}`}
                        onClick={(e) => handleAddMusicToPanel(track, e)}
                    >
                        {panelMusicIds.has(String(track.id)) ? 'Уже в панели' : 'В панель'}
                    </button>
                </div>
            ))}
        </div>
        
        {hasMoreTracks && (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '20px'}}>
                <button 
                    onClick={loadMoreTracks} 
                    disabled={loadingMoreTracks}
                    style={{
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'white',
                        padding: '10px 30px',
                        borderRadius: '20px',
                        cursor: loadingMoreTracks ? 'wait' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                    }}
                >
                    {loadingMoreTracks ? 'Загрузка...' : 'Показать еще'}
                </button>
            </div>
        )}
      </div>
    </>
  )};

  const renderArtist = () => {
    if (!selectedArtist) return null;
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{selectedArtist.name}</h2>
        </div>
        <p style={{color: '#b3b3b3', marginBottom: '20px'}}>{selectedArtist.bio}</p>
        
        <h3 className={styles.sectionTitle} style={{marginBottom: '15px', fontSize: '20px'}}>Альбомы</h3>
        <div className={styles.grid}>
          {selectedArtist.albums && selectedArtist.albums.map(album => (
            <div key={album.id} className={styles.albumCard} onClick={() => handleAlbumClick(album.id)}>
              <div className={styles.albumImageWrapper}>
                <img src={album.cover || '/placeholder-album.png'} alt={album.title} className={styles.albumImage} />
              </div>
              <div className={styles.albumTitle}>{album.title}</div>
              <div className={styles.albumArtist}>{album.release_date}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAlbum = () => {
    if (!selectedAlbum) return null;
    return (
      <div className={styles.section}>
        <div style={{display: 'flex', gap: '30px', marginBottom: '30px', alignItems: 'flex-end', flexWrap: 'wrap'}}>
            <img 
                src={selectedAlbum.cover || '/placeholder-album.png'} 
                alt={selectedAlbum.title} 
                style={{width: '230px', height: '230px', objectFit: 'cover', boxShadow: '0 4px 60px rgba(0,0,0,0.5)'}}
            />
            <div>
                <div style={{textTransform: 'uppercase', fontSize: '12px', fontWeight: 'bold'}}>Альбом</div>
                <h1 style={{fontSize: '48px', fontWeight: '900', margin: '10px 0', lineHeight: '1'}}>{selectedAlbum.title}</h1>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '600'}}>
                    <span>{selectedAlbum.artist_name}</span>
                    <span>•</span>
                    <span style={{color: '#b3b3b3'}}>{selectedAlbum.release_date?.split('-')[0]}</span>
                    <span>•</span>
                    <span style={{color: '#b3b3b3'}}>{selectedAlbum.tracks?.length} треков</span>
                </div>
            </div>
        </div>

        <div className={styles.trackList}>
            <div className={styles.trackRow} style={{cursor: 'default', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '10px'}}>
                <div className={styles.trackNumberContainer} style={{color: '#b3b3b3', fontSize: '14px'}}>#</div>
                <div className={styles.trackInfo} style={{color: '#b3b3b3', fontSize: '14px', textTransform: 'uppercase'}}>Название</div>
                <div style={{color: '#b3b3b3', fontSize: '14px'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg></div>
            </div>
            {selectedAlbum.tracks && selectedAlbum.tracks.map((track, index) => (
                <div 
                    key={track.id} 
                    className={`${styles.trackRow} ${currentTrack?.id === track.id ? styles.active : ''}`}
                    onClick={() => playTrack(track, selectedAlbum.tracks)}
                >
                    <div className={styles.trackNumberContainer}>
                        <span className={styles.trackNumber}>{index + 1}</span>
                        <span className={currentTrack?.id === track.id && isPlaying ? styles.playingIcon : styles.playIcon}>
                             {currentTrack?.id === track.id && isPlaying ? <PauseIcon size={16}/> : <PlayIcon size={16}/>}
                        </span>
                    </div>
                    <div className={styles.trackInfo}>
                        <div className={styles.trackTitle}>{track.title}</div>
                        <div className={styles.trackArtist}>{selectedAlbum.artist_name}</div>
                    </div>
                    <div className={styles.trackDuration}>
                        {formatTime(track.duration)}
                    </div>
                    <button
                        className={`${styles.addToPanelButton} ${panelMusicIds.has(String(track.id)) ? styles.addToPanelButtonAdded : ''}`}
                        onClick={(e) => handleAddMusicToPanel(track, e)}
                    >
                        {panelMusicIds.has(String(track.id)) ? 'Уже в панели' : 'В панель'}
                    </button>
                </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      
      <div className={styles.container}>
        <div className={styles.header}>
            {view === 'home' ? (
                <h1 className={styles.title}>Музыка</h1>
            ) : (
                <button 
                  onClick={handleBack}
                  className={styles.backButton}
                >
                  ← Назад
                </button>
            )}
            {!embedded && <div onClick={goBack} style={{cursor: 'pointer'}}>Закрыть</div>}
        </div>

        {view === 'home' && (
            <div className={styles.searchContainer}>
                <div className={styles.searchIcon}><SearchIcon /></div>
                <input 
                    type="text" 
                    className={styles.searchInput} 
                    placeholder="Что хотите послушать?" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>
        )}

        {loading ? (
             <div className={styles.loader}><Loader/></div> 
        ) : error ? (
            <div className={styles.error}>{error}</div>
        ) : (
            <>
                {view === 'home' && renderHome()}
                {view === 'artist' && renderArtist()}
                {view === 'album' && renderAlbum()}
            </>
        )}
      </div>

      {/* Player Footer */}
      {currentTrack && (
        <div className={styles.playerBar}>
            <div className={styles.playerLeft}>
                <img 
                    src={currentTrack.album_cover || '/placeholder-album.png'} 
                    alt="cover" 
                    className={styles.currentTrackImage} 
                />
                <div className={styles.currentTrackInfo}>
                    <div className={styles.currentTrackTitle}>{currentTrack.title}</div>
                    <div className={styles.currentTrackArtist}>{currentTrack.artist_name}</div>
                </div>
            </div>

            <div className={styles.playerCenter}>
                <div className={styles.playerControls}>
                    <button className={styles.controlButton} onClick={playPrev}><SkipBackIcon /></button>
                    <button className={`${styles.controlButton} ${styles.playPause}`} onClick={togglePlay}>
                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <button className={styles.controlButton} onClick={playNext}><SkipForwardIcon /></button>
                </div>
                <div className={styles.progressBarContainer}>
                    <span>{formatTime(currentTime)}</span>
                    <div className={styles.progressBar} onClick={(e) => {
                         const bounds = e.currentTarget.getBoundingClientRect();
                         const percent = (e.clientX - bounds.left) / bounds.width;
                         audioRef.current.currentTime = percent * duration;
                         setCurrentTime(percent * duration);
                    }}>
                        <div className={styles.progressFill} style={{width: `${(currentTime / duration) * 100}%`}}></div>
                    </div>
                    <span>{formatTime(duration)}</span>
                </div>
            </div>

            <div className={styles.playerRight}>
                 <div className={styles.volumeContainer}>
                     <VolumeIcon size={20} style={{color: '#b3b3b3'}}/>
                     <input 
                        type="range" 
                        min="0" 
                        max="1" 
                        step="0.01" 
                        value={volume} 
                        onChange={handleVolumeChange}
                        className={styles.volumeSlider}
                     />
                 </div>
            </div>
        </div>
      )}
    </div>
  )
}

export default MusicPage
